"use client"

import { createContext, useContext, ReactElement, ReactNode } from "react"
import {
  completedShipmentStat,
  landingFooterLabels,
  CompletedShipmentValue,
  ShipmentConcept,
  ShipmentStats,
} from "@/lib/completed-shipments"

export const CompletedShipmentsContext = createContext<{
  data: ShipmentStats | null
  pagePath?: string
  concept: ShipmentConcept | null
}>({ data: null, concept: null })
export function useCompletedShipmentStat(
  concept?: ShipmentConcept,
  period: "allTime" | "last12Months" = "allTime",
  metric: "matches" | "listings" = "matches",
): CompletedShipmentValue | null {
  const context = useContext(CompletedShipmentsContext)
  const selected = concept ?? context.concept

  if (!selected) {
    return null
  }

  const data =
    period === "last12Months"
      ? context.data?.last12Months ?? null
      : context.data

  return completedShipmentStat(data, selected, metric)
}
export function CompletedShipmentStat({
  concept,
  className,
  methodology = false,
  numberOnly = false,
  period = "allTime",
  metric = "matches",
  children,
}: {
  concept?: ShipmentConcept
  className?: string
  methodology?: boolean
  numberOnly?: boolean
  period?: "allTime" | "last12Months"
  metric?: "matches" | "listings"
  children?: (count: string, plain: string) => ReactNode
}): ReactElement | null {
  const stat = useCompletedShipmentStat(concept, period, metric)

  if (!stat) {
    return null
  }

  const defaultLabel = numberOnly ? stat.formatted : stat.text

  return (
    <span className={className} data-completed-shipment-stat>
      {children ? children(stat.formatted, stat.plain) : defaultLabel}
      {methodology && (
        <small className="block mt-2 font-normal">
          Based on available CitizenShipper{" "}
          {metric === "listings" ? "listing" : "booking"} records from{" "}
          {stat.coveredFrom} through {stat.coveredTo}.
        </small>
      )}
    </span>
  )
}

type ProofCopy = ((count: string, plain: string) => string) | undefined

// Approved footer wording, most specific route first.
function footerProofCopy(
  pagePath: string,
  concept: ShipmentConcept | null,
): ProofCopy {
  const isPetLocationPage =
    /^\/pet-shipping\/[^/]+(?:\/[^/]+)?$/.test(pagePath) &&
    pagePath !== "/pet-shipping/all-cities"
  const isDogBreedPage =
    /^\/dog-shipping\/[^/]+$/.test(pagePath) &&
    pagePath !== "/dog-shipping/all-breeds"
  const isBoatCityPage = /^\/boat-transportation\/[^/]+\/[^/]+$/.test(pagePath)
  const isVehicleCityPage = /^\/vehicle-transportation\/[^/]+\/[^/]+$/.test(
    pagePath,
  )
  const isPlatformTotalPage =
    pagePath === "/package-delivery" || /^\/drivers\/\d+$/.test(pagePath)
  const isCostToShipMetroPage = /^\/cost-to-ship\/[^/]+$/.test(pagePath)

  if (landingFooterLabels[pagePath]) {
    return (count) => `${count} ${landingFooterLabels[pagePath]}`
  }

  if (pagePath === "/rv-transport") {
    return (_count, plain) =>
      `At least ${plain} RV deliveries completed nationwide through CitizenShipper.`
  }

  if (isPlatformTotalPage) {
    return (count) =>
      `CitizenShipper: ${count} shipments delivered nationwide since 2008.`
  }

  if (pagePath === "/drivers") {
    const delivered =
      concept === "pets" ? "pets safely transported" : "shipments delivered"

    return (_count, plain) =>
      `At least ${plain} ${delivered} nationwide last year.`
  }

  if (pagePath === "/boat-transportation" || isBoatCityPage) {
    return (count) =>
      `${count} boats delivered nationwide through CitizenShipper since 2008.`
  }

  if (pagePath === "/how-it-works" || isCostToShipMetroPage) {
    return (count) =>
      `${count} shipments delivered nationwide through CitizenShipper since 2008.`
  }

  if (pagePath === "/vehicle-transportation" || isVehicleCityPage) {
    return (count) =>
      `${count} vehicles delivered nationwide through CitizenShipper since 2008.`
  }

  if (pagePath === "/new-drivers") {
    return (count) => `${count} shipments listed on CitizenShipper since 2008.`
  }

  if (pagePath === "/breeders") {
    return (count) => `${count} pets safely transported last year.`
  }

  if (pagePath === "/partners") {
    return (count) => `${count} pets transported last year.`
  }

  if (isDogBreedPage) {
    return (count) => `${count} dogs safely transported nationwide since 2008.`
  }

  if (isPetLocationPage) {
    return (count) => `${count} pets safely transported nationwide since 2008.`
  }

  if (pagePath === "/pet-transportation") {
    return (count) => `${count} pets safely transported since 2008.`
  }

  if (concept === "household") {
    return (count) => `${count} household deliveries`
  }

  if (concept === "all") {
    return (count) =>
      `${count} shipments delivered through CitizenShipper since 2008.`
  }

  return undefined
}

export function CompletedShipmentProof(): ReactElement | null {
  const { concept, pagePath } = useContext(CompletedShipmentsContext)
  const period =
    pagePath === "/partners" ||
    pagePath === "/breeders" ||
    pagePath === "/drivers"
      ? "last12Months"
      : "allTime"
  const metric = pagePath === "/new-drivers" ? "listings" : "matches"
  const stat = useCompletedShipmentStat(undefined, period, metric)

  if (!stat) {
    return null
  }

  return (
    <aside
      data-completed-shipment-proof
      className="bg-blue-50 text-blue-950 px-6 py-6 text-center font-semibold"
      aria-label={
        metric === "listings"
          ? "CitizenShipper shipment listings"
          : "CitizenShipper completed shipments"
      }
    >
      <CompletedShipmentStat methodology period={period} metric={metric}>
        {footerProofCopy(pagePath ?? "", concept)}
      </CompletedShipmentStat>
    </aside>
  )
}
